/**
 * Estoque de materiais: o que ela compra, vende e gasta nos atendimentos.
 * A quantidade de cada produto é a soma dos movimentos (entrada, venda, gasto e correção), sempre na
 * medida do produto (unidade, ml ou g). Assim dois aparelhos podem lançar movimentos ao mesmo tempo
 * sem um apagar o outro na sincronização.
 */
import type { Appointment, Client, Payment, Product, Sale, Service, ServiceSupply, StockMovement, StockUnit } from '../schemas/entities';
import { formatMoneyShort } from './money';
import { type LocalDate, toMs, utcToZoned } from './time';

export const UNIT_LABELS: Record<StockUnit, { one: string; many: string }> = {
  un: { one: 'unidade', many: 'unidades' },
  ml: { one: 'ml', many: 'ml' },
  g: { one: 'g', many: 'g' },
};

export interface StockLevel {
  product: Product;
  /** na medida do produto */
  qty: number;
  /** em embalagens (12 un = 12; 850 ml num frasco de 500 ml = 1,7) */
  packs: number;
  /** custo médio por embalagem, das entradas; null sem entrada com preço */
  avgCostPerPackCents: number | null;
  /** quanto vale o que tem, pelo custo médio */
  valueCents: number;
  low: boolean;
  out: boolean;
}

const alive = (m: { deleted_at: string | null }) => !m.deleted_at;

/** Quantidade de cada produto, somando os movimentos. */
export function stockByProduct(movements: StockMovement[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const m of movements) {
    if (!alive(m)) continue;
    out.set(m.product_id, (out.get(m.product_id) ?? 0) + m.qty);
  }
  return out;
}

/** Custo médio por unidade da medida (centavos por ml, por exemplo), pelas entradas. */
export function averageUnitCost(movements: StockMovement[], productId: string): number | null {
  let qty = 0;
  let cost = 0;
  for (const m of movements) {
    if (!alive(m) || m.product_id !== productId || m.kind !== 'in' || m.qty <= 0 || m.cost_cents <= 0) continue;
    qty += m.qty;
    cost += m.cost_cents;
  }
  return qty > 0 ? cost / qty : null;
}

export function stockLevel(product: Product, movements: StockMovement[]): StockLevel {
  const qty = movements.reduce((sum, m) => (alive(m) && m.product_id === product.id ? sum + m.qty : sum), 0);
  const unitCost = averageUnitCost(movements, product.id) ?? (product.cost_cents > 0 ? product.cost_cents / product.pack_size : null);
  return {
    product,
    qty,
    packs: qty / product.pack_size,
    avgCostPerPackCents: unitCost === null ? null : Math.round(unitCost * product.pack_size),
    valueCents: unitCost === null ? 0 : Math.round(Math.max(0, qty) * unitCost),
    low: product.min_qty !== null && qty > 0 && qty <= product.min_qty,
    out: qty <= 0,
  };
}

/** Os produtos ativos com a quantidade de cada um: primeiro o que acabou, depois o que está acabando. */
export function stockLevels(products: Product[], movements: StockMovement[]): StockLevel[] {
  const rank = (l: StockLevel) => (l.out ? 0 : l.low ? 1 : 2);
  return products
    .filter((p) => alive(p) && p.active)
    .map((p) => stockLevel(p, movements))
    .sort((a, b) => rank(a) - rank(b) || a.product.name.localeCompare(b.product.name, 'pt-BR'));
}

/** "12 unidades", "1 unidade", "850 ml", "40 g"; curto: "12 un.". */
export function formatStockQty(unit: StockUnit, qty: number, short = false): string {
  const n = Math.round(qty);
  const label = UNIT_LABELS[unit];
  const num = n.toLocaleString('pt-BR');
  if (unit === 'un') return short ? `${num} un.` : `${num} ${Math.abs(n) === 1 ? label.one : label.many}`;
  return `${num} ${label.one}`;
}

/** Nome da embalagem pela medida: frasco (ml), pote (g). */
export const PACK_NAMES: Record<Exclude<StockUnit, 'un'>, { one: string; many: string }> = {
  ml: { one: 'frasco', many: 'frascos' },
  g: { one: 'pote', many: 'potes' },
};

/** "frasco de 500 ml", "pote de 30 g"; null para unidade. */
export function packLabel(product: Pick<Product, 'unit' | 'pack_size'>): string | null {
  if (product.unit === 'un') return null;
  return `${PACK_NAMES[product.unit].one} de ${formatStockQty(product.unit, product.pack_size)}`;
}

/** "2 frascos", "1 pote", "3 unidades": quantas embalagens. */
export function formatPackCount(product: Pick<Product, 'unit'>, packs: number): string {
  const n = Math.round(packs * 10) / 10;
  const text = n.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  if (product.unit === 'un') return `${text} ${n === 1 ? 'unidade' : 'unidades'}`;
  const names = PACK_NAMES[product.unit];
  return `${text} ${n === 1 ? names.one : names.many}`;
}

/** Linha de descrição: "Pote de 30 g · só uso nos atendimentos", "Vende por R$ 15". */
export function productKindLabel(product: Pick<Product, 'unit' | 'pack_size' | 'price_cents'>): string {
  const text = [packLabel(product), product.price_cents === null ? 'só uso nos atendimentos' : null].filter(Boolean).join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Em embalagens, com uma casa: "1,7 embalagem", "2 embalagens". Só faz sentido com pack_size > 1. */
export function formatPacks(packs: number): string {
  const rounded = Math.round(packs * 10) / 10;
  const text = rounded.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  return `${text} ${Math.abs(rounded) === 1 ? 'embalagem' : 'embalagens'}`;
}

export interface SaleItemInput {
  productId: string;
  /** embalagens vendidas (1 esmalte, 2 cremes) */
  packs: number;
}

/** Total da venda, pelos preços de venda de cada produto. Produto sem preço de venda entra como 0. */
export function saleTotal(items: SaleItemInput[], products: Map<string, Product>): number {
  return items.reduce((sum, i) => sum + Math.max(0, i.packs) * (products.get(i.productId)?.price_cents ?? 0), 0);
}

/** O que um serviço gasta de cada produto. */
export function suppliesOf(serviceId: string, supplies: ServiceSupply[]): ServiceSupply[] {
  return supplies.filter((s) => alive(s) && s.service_id === serviceId);
}

export interface SaleReceivable {
  sale: Sale;
  due: number;
  paid: number;
  missing: number;
}

/** Vendas de produto com valor em aberto ("receber depois"). */
export function saleReceivables(sales: Sale[], payments: Payment[]): SaleReceivable[] {
  const paid = new Map<string, number>();
  for (const p of payments) {
    if (p.deleted_at || !p.sale_id) continue;
    paid.set(p.sale_id, (paid.get(p.sale_id) ?? 0) + p.amount_cents);
  }
  const out: SaleReceivable[] = [];
  for (const s of sales) {
    if (!alive(s)) continue;
    const p = paid.get(s.id) ?? 0;
    if (p < s.total_cents) out.push({ sale: s, due: s.total_cents, paid: p, missing: s.total_cents - p });
  }
  return out.sort((x, y) => toMs(y.sale.sold_at) - toMs(x.sale.sold_at));
}

/** Margem por embalagem: preço de venda menos o custo médio. */
export function marginPerPack(level: Pick<StockLevel, 'product' | 'avgCostPerPackCents'>): number | null {
  if (level.product.price_cents === null || level.avgCostPerPackCents === null) return null;
  return level.product.price_cents - level.avgCostPerPackCents;
}

/** Valor do estoque inteiro, pelo custo médio. */
export function stockValue(levels: StockLevel[]): number {
  return levels.reduce((sum, l) => sum + l.valueCents, 0);
}

/** Custo médio por unidade da medida, com o preço do produto quando ainda não há entrada com preço. */
export function unitCostOf(product: Product, movements: StockMovement[]): number | null {
  return averageUnitCost(movements, product.id) ?? (product.cost_cents > 0 ? product.cost_cents / product.pack_size : null);
}

/** Custo do material que um serviço gasta em cada atendimento, em centavos (sem custo conhecido conta 0). */
export function materialCost(serviceId: string, supplies: ServiceSupply[], products: Product[], movements: StockMovement[]): number {
  const byId = new Map(products.map((p) => [p.id, p]));
  let total = 0;
  for (const s of suppliesOf(serviceId, supplies)) {
    const p = byId.get(s.product_id);
    if (!p || p.deleted_at) continue;
    total += s.qty * (unitCostOf(p, movements) ?? 0);
  }
  return Math.round(total);
}

/** Custo do material de uma lista ainda não salva (prévia na folha do serviço), em centavos. */
export function materialCostOf(items: Array<{ product: Product; qty: number }>, movements: StockMovement[]): number {
  let total = 0;
  for (const i of items) if (i.qty > 0) total += i.qty * (unitCostOf(i.product, movements) ?? 0);
  return Math.round(total);
}

export interface ProductUse {
  supply: ServiceSupply;
  service: Service;
  /** atendimentos concluídos desse serviço nos últimos 60 dias */
  recent: number;
}

/** Serviços que gastam o produto, do mais feito para o menos feito. */
export function usesOf(productId: string, supplies: ServiceSupply[], services: Service[], appointments: Appointment[], now: number): ProductUse[] {
  const since = now - 60 * 86_400_000;
  const serviceById = new Map(services.map((s) => [s.id, s]));
  const out: ProductUse[] = [];
  for (const supply of supplies) {
    if (!alive(supply) || supply.product_id !== productId) continue;
    const service = serviceById.get(supply.service_id);
    if (!service || service.deleted_at || !service.active) continue;
    const recent = appointments.filter((a) => !a.deleted_at && a.status === 'completed' && a.service_id === service.id && toMs(a.start_utc) >= since).length;
    out.push({ supply, service, recent });
  }
  return out.sort((a, b) => b.recent - a.recent || a.supply.qty - b.supply.qty || a.service.name.localeCompare(b.service.name, 'pt-BR'));
}

/** "Dá para mais 5 manutenções": quantos atendimentos do serviço mais feito ainda cabem no que tem. */
export function usesLeft(qty: number, uses: ProductUse[]): { service: Service; count: number } | null {
  const main = uses[0];
  if (!main || qty <= 0) return null;
  return { service: main.service, count: Math.floor(qty / main.supply.qty) };
}

export interface MovementRow {
  movement: StockMovement;
  date: LocalDate;
  /** cliente (gasto ou venda), "Entrada: 2 potes" ou "Correção da contagem" */
  title: string;
  /** serviço do atendimento, "Vendido", o valor pago ou a observação */
  detail: string | null;
}

/** Últimos movimentos de um produto, do mais novo para o mais antigo. */
export function movementRows(
  product: Product,
  movements: StockMovement[],
  ctx: { appointments: Appointment[]; clients: Client[]; services: Service[]; sales: Sale[]; timeZone: string },
): MovementRow[] {
  const appointmentById = new Map(ctx.appointments.map((a) => [a.id, a]));
  const clientById = new Map(ctx.clients.map((c) => [c.id, c]));
  const serviceById = new Map(ctx.services.map((s) => [s.id, s]));
  const saleById = new Map(ctx.sales.map((s) => [s.id, s]));
  return movements
    .filter((m) => alive(m) && m.product_id === product.id)
    .sort((a, b) => toMs(b.occurred_at) - toMs(a.occurred_at))
    .map((m) => {
      const date = utcToZoned(m.occurred_at, ctx.timeZone).date;
      const appt = m.appointment_id ? appointmentById.get(m.appointment_id) : undefined;
      const sale = m.sale_id ? saleById.get(m.sale_id) : undefined;
      const clientId = appt?.client_id ?? sale?.client_id ?? null;
      const clientName = clientId ? (clientById.get(clientId)?.name ?? null) : null;
      if (m.kind === 'in') {
        const packs = m.qty / product.pack_size;
        return { movement: m, date, title: `Entrada: ${formatPackCount(product, packs)}`, detail: m.cost_cents > 0 ? formatMoneyShort(m.cost_cents) : m.note };
      }
      if (m.kind === 'adjust') return { movement: m, date, title: 'Correção da contagem', detail: m.note };
      if (m.kind === 'sale') return { movement: m, date, title: clientName ?? 'Venda', detail: 'Vendido' };
      return { movement: m, date, title: clientName ?? 'Atendimento', detail: appt ? (serviceById.get(appt.service_id)?.name ?? null) : null };
    });
}

/** "−4 g", "+60 g", "−1 un." */
export function formatMovementQty(unit: StockUnit, qty: number): string {
  const sign = qty > 0 ? '+' : qty < 0 ? '−' : '';
  return `${sign}${formatStockQty(unit, Math.abs(qty), true)}`;
}

export interface ProductSalesSummary {
  totalCents: number;
  costCents: number;
  profitCents: number;
  /** embalagens vendidas */
  count: number;
}

/** Vendas de produto entre from e to (datas locais), com o custo pelo custo médio das entradas. */
export function productSales(products: Product[], movements: StockMovement[], from: LocalDate, to: LocalDate, timeZone: string): ProductSalesSummary {
  const byId = new Map(products.map((p) => [p.id, p]));
  let totalCents = 0;
  let cost = 0;
  let count = 0;
  for (const m of movements) {
    if (!alive(m) || m.kind !== 'sale') continue;
    const date = utcToZoned(m.occurred_at, timeZone).date;
    if (date < from || date > to) continue;
    const p = byId.get(m.product_id);
    totalCents += m.price_cents;
    if (p) {
      cost += -m.qty * (unitCostOf(p, movements) ?? 0);
      count += -m.qty / p.pack_size;
    }
  }
  const costCents = Math.round(cost);
  return { totalCents, costCents, profitCents: totalCents - costCents, count: Math.round(count) };
}

/** "Creme para mãos", "Creme para mãos e mais 1": o que foi vendido numa venda. */
export function saleItemsLabel(saleId: string, movements: StockMovement[], products: Product[]): string {
  const byId = new Map(products.map((p) => [p.id, p]));
  const names: string[] = [];
  for (const m of movements) {
    if (!alive(m) || m.sale_id !== saleId || m.kind !== 'sale') continue;
    const name = byId.get(m.product_id)?.name;
    if (name && !names.includes(name)) names.push(name);
  }
  if (names.length === 0) return 'Produto';
  return names.length === 1 ? names[0]! : `${names[0]} e mais ${names.length - 1}`;
}

/** Produtos que dá para vender agora: com preço de venda e com estoque. */
export function sellable(levels: StockLevel[]): StockLevel[] {
  return levels.filter((l) => l.product.price_cents !== null && l.qty >= l.product.pack_size).sort((a, b) => a.product.name.localeCompare(b.product.name, 'pt-BR'));
}
